import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { InputText } from "primereact/inputtext";
import { OverlayPanel } from "primereact/overlaypanel";

import VariableHighlightEditor, {
	VariableHighlightEditorHandle,
} from "../variable-editor/VariableHighlightEditor";

import "./variable-input.css";

export interface VariableOption {
	name: string;
	type?: string;
}

export interface VariableGroup {
	label: string;
	items: VariableOption[];
}

interface VariableInputProps {
	value: string;
	onChange: (value: string) => void;
	/** Grouped variables offered by the inserter (e.g. Campaign, Participant). */
	groups: VariableGroup[];
	/**
	 * Names highlighted as "known" in the editor. Defaults to every variable in
	 * `groups`, so anything else the user types shows as an unknown (amber) token.
	 */
	knownVariables?: string[];
	placeholder?: string;
	id?: string;
	/** Single-line height; the editor still wraps long content. */
	minHeight?: number;
}

/**
 * A single-line text input with `{{variable}}` highlighting and an attached
 * picker that inserts variables at the caret. Reuses the same editor and token
 * styling as the message-template editor, so personalization works identically
 * across campaign/message properties.
 */
const VariableInput = ({
	value,
	onChange,
	groups,
	knownVariables,
	placeholder,
	id,
	minHeight = 40,
}: VariableInputProps) => {
	const { t } = useTranslation("common");
	const editorRef = useRef<VariableHighlightEditorHandle>(null);
	const overlayRef = useRef<OverlayPanel>(null);
	const [search, setSearch] = useState("");

	const known = useMemo(
		() => knownVariables ?? groups.flatMap((group) => group.items.map((item) => item.name)),
		[knownVariables, groups]
	);

	const filteredGroups = useMemo(() => {
		const needle = search.trim().toLowerCase();
		if (!needle) return groups;
		return groups
			.map((group) => ({
				...group,
				items: group.items.filter((item) => item.name.toLowerCase().includes(needle)),
			}))
			.filter((group) => group.items.length > 0);
	}, [groups, search]);

	const insert = (name: string) => {
		editorRef.current?.insertVariable(name);
		overlayRef.current?.hide();
		setSearch("");
	};

	return (
		<div className="vi-container">
			<VariableHighlightEditor
				ref={editorRef}
				id={id}
				value={value}
				onChange={onChange}
				knownVariables={known}
				placeholder={placeholder}
				minHeight={minHeight}
			/>
			<button
				type="button"
				className="vi-insert-button"
				aria-label={t("MARKETING_CAMPAIGNS.VARIABLES.INSERT")}
				title={t("MARKETING_CAMPAIGNS.VARIABLES.INSERT")}
				onClick={(event) => overlayRef.current?.toggle(event)}
			>
				<i className="pi pi-plus" />
			</button>

			<OverlayPanel
				ref={overlayRef}
				className="vi-overlay"
				dismissable
				onHide={() => setSearch("")}
			>
				<span className="p-input-icon-left vi-search">
					<i className="pi pi-search" />
					<InputText
						value={search}
						onChange={(event) => setSearch(event.target.value)}
						placeholder={t("MARKETING_CAMPAIGNS.VARIABLES.SEARCH_PLACEHOLDER")}
						autoFocus
					/>
				</span>

				<div className="vi-groups">
					{filteredGroups.length === 0 && (
						<p className="vi-empty">{t("MARKETING_CAMPAIGNS.VARIABLES.EMPTY")}</p>
					)}
					{filteredGroups.map((group) => (
						<div
							key={group.label}
							className="vi-group"
						>
							<div className="vi-group-label">{group.label}</div>
							<div className="vi-chips">
								{group.items.map((item) => (
									<button
										key={`${group.label}:${item.name}`}
										type="button"
										className="vi-chip"
										title={item.type}
										onClick={() => insert(item.name)}
									>
										<i className="pi pi-plus vi-chip-icon" />
										{`{{${item.name}}}`}
									</button>
								))}
							</div>
						</div>
					))}
				</div>
			</OverlayPanel>
		</div>
	);
};

export default VariableInput;
